import { defineStore } from 'pinia';
import { ref } from 'vue';
import { ApiError, type PublicEvent, type WsClientFrame } from '@neon-one/auctions-types';

import { httpClient } from '@/shell/http';

export const EventLifecycleFrame = {
    Opened: 'event.opened', Paused: 'event.paused', Resumed: 'event.resumed',
    Closed: 'event.closed', Archived: 'event.archived', Extended: 'event.extended',
} as const;
export const AuctionStatus = { Open: 'open', Closed: 'closed' } as const;
export const ShellRoute = { NotFound: 'not-found' } as const;
export const HTTP_NOT_FOUND = 404;
const EVENT_LOAD_ERROR = 'Unable to load auction.';
const EVENT_NOT_FOUND_ERROR = 'Auction not found.';

export const useAuctionEvent = defineStore('auctionEvent', () => {
    const slug = ref<string | null>(null);
    const event = ref<PublicEvent | null>(null);
    const loading = ref(false);
    const error = ref<string | null>(null);
    let replaceWithNotFound: (() => Promise<unknown>) | null = null;

    function setNotFoundHandler(handler: () => Promise<unknown>): void {
        replaceWithNotFound = handler;
    }

    async function markNotFound(): Promise<void> {
        clear();
        error.value = EVENT_NOT_FOUND_ERROR;
        await replaceWithNotFound?.();
    }

    async function loadBySlug(eventSlug: string, force = false): Promise<PublicEvent> {
        if (!force && slug.value === eventSlug && event.value) return event.value;
        loading.value = true;
        error.value = null;
        try {
            const { data } = await httpClient().get<PublicEvent>(`/public/events/${encodeURIComponent(eventSlug)}`);
            event.value = data;
            slug.value = eventSlug;
            return data;
        } catch (failure) {
            if (failure instanceof ApiError && failure.status === HTTP_NOT_FOUND) {
                clear();
                error.value = EVENT_NOT_FOUND_ERROR;
            } else {
                error.value = EVENT_LOAD_ERROR;
            }
            throw failure;
        } finally {
            loading.value = false;
        }
    }

    function clear(): void {
        slug.value = null;
        event.value = null;
        error.value = null;
    }

    function setMockEvent(patch: Partial<PublicEvent>): void {
        if (!event.value) return;
        event.value = { ...event.value, ...patch };
    }

    function applyFrame(frame: WsClientFrame): void {
        if (!event.value) return;
        if (frame.type === EventLifecycleFrame.Opened) {
            event.value = { ...event.value, status: AuctionStatus.Open };
        } else if (frame.type === EventLifecycleFrame.Paused) {
            event.value.isPaused = true;
        } else if (frame.type === EventLifecycleFrame.Resumed) {
            event.value.isPaused = false;
            const payload = frame.payload as { auctionCloseAt?: string } | undefined;
            if (payload?.auctionCloseAt) event.value.closeAt = payload.auctionCloseAt;
        } else if (frame.type === EventLifecycleFrame.Closed) {
            event.value = { ...event.value, status: AuctionStatus.Closed };
        } else if (frame.type === EventLifecycleFrame.Archived) {
            // The bidder site is gone. A quiet close sends this instead of event.closed.
            // eslint-disable-next-line local-rules/no-floating-side-effect -- WebSocket callback cannot await.
            markNotFound().catch(failure => console.error('[auctionEvent] archived navigation failed', failure));
        } else if (frame.type === EventLifecycleFrame.Extended) {
            const payload = frame.payload as { newCloseAt: string } | undefined;
            if (payload?.newCloseAt) event.value.closeAt = payload.newCloseAt;
        }
    }

    return {
        slug, event, loading, error, loadBySlug, clear, setMockEvent, applyFrame, setNotFoundHandler, markNotFound,
    };
});
